import { createBrowserHistory } from 'history';
import { defer, startsWith } from 'lodash/fp';
import { prependAppPathToEmbbeddedWicketPages } from '../react/components/Link/service';

export type LocationStateWithBackground = { backgroundLocation?: Location; modalRouteDepth?: number } | undefined;

type Callback = (preventNavigation: boolean) => void;
interface ConfirmationHandler {
    (message: string, callback: Callback): void;
}

const history = isSinglePageApp()
    ? createBrowserHistory({
          basename: '/app',
          getUserConfirmation: getConfirmation,
      })
    : undefined;
export default history;

const config = {
    getConfirmation: defaultConfirmation,
};

function defaultConfirmation(message: string, callback: Callback) {
    callback(window.confirm(message)); // eslint-disable-line no-alert
}

function getConfirmation(message: string, callback: Callback) {
    config.getConfirmation(message, callback);
}

export function registerConfirmationHandler(handler: ConfirmationHandler) {
    config.getConfirmation = handler;
    return function deregisterConfirmation() {
        config.getConfirmation = defaultConfirmation;
    };
}

let pageReloadScheduled = false;

export function schedulePageReload() {
    pageReloadScheduled = true;
}

export function isPageReloadScheduled() {
    return pageReloadScheduled;
}

export function isSinglePageApp() {
    const { pathname } = window.location;
    return startsWith('/app/', pathname) || pathname === '/app';
}

export function getStateForBackgroundRoute() {
    const location = history!.location;
    const state = location.state as LocationStateWithBackground;
    const backgroundLocation = state?.backgroundLocation;
    return {
        backgroundLocation: backgroundLocation || location,
        modalRouteDepth: (state?.modalRouteDepth ?? 0) + 1,
    };
}

const getAnchor = (el: Node): HTMLAnchorElement | null =>
    el instanceof HTMLAnchorElement ? el : el.parentNode && getAnchor(el.parentNode);

export const globalClickHandler = (event: MouseEvent) => {
    if (!(event.target instanceof Node)) {
        return;
    }

    const a = getAnchor(event.target);

    if (!a) {
        return;
    }

    const href = a.getAttribute('href');

    if (
        !href ||
        a.dataset.reactLink ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.button !== 0 ||
        isPageReloadScheduled()
    ) {
        return;
    }

    const path = prependAppPathToEmbbeddedWicketPages(href);
    const isApp = path && path.indexOf('/app/') === 0;

    if (!isApp || event.defaultPrevented) {
        return;
    }

    event.preventDefault();

    defer(() =>
        history!.push(path.replace('/app', ''), a.dataset.openInModal ? getStateForBackgroundRoute() : undefined)
    );
};
